import { CONSENT_COOKIE_NAME } from './constants.js';
import {
  decodeConsentCookie,
  encodeConsentCookie,
} from './cookie-codec.js';
import type { ConsentCookieDecodeResult } from './cookie-codec.js';
import type { ConsentState } from './types.js';

const SECONDS_PER_DAY = 86_400;
const COOKIE_NAME_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

export const MAX_CONSENT_LIFETIME_DAYS = 36_500;

export interface CookieDocument {
  cookie: string;
}

export interface ConsentCookieStorageOptions {
  readonly consentLifetimeDays: number;
  readonly cookieName?: string;
  readonly secure?: boolean;
  readonly document?: CookieDocument;
  readonly now?: () => Date;
}

export interface ConsentCookieStorage {
  read(): ConsentCookieDecodeResult;
  write(state: ConsentState): void;
  clear(): void;
}

export type ConsentCookieStorageErrorCode =
  | 'invalid_cookie_name'
  | 'invalid_lifetime'
  | 'invalid_now'
  | 'unavailable_document';

export class ConsentCookieStorageError extends Error {
  readonly code: ConsentCookieStorageErrorCode;

  constructor(code: ConsentCookieStorageErrorCode, message: string) {
    super(message);
    this.name = 'ConsentCookieStorageError';
    this.code = code;
  }
}

function validateOptions(options: ConsentCookieStorageOptions): void {
  const cookieName = options.cookieName ?? CONSENT_COOKIE_NAME;

  if (!COOKIE_NAME_PATTERN.test(cookieName)) {
    throw new ConsentCookieStorageError(
      'invalid_cookie_name',
      'Cookie name must contain 1-64 ASCII letters, numbers, underscores or hyphens.',
    );
  }

  if (
    !Number.isSafeInteger(options.consentLifetimeDays) ||
    options.consentLifetimeDays < 1 ||
    options.consentLifetimeDays > MAX_CONSENT_LIFETIME_DAYS
  ) {
    throw new ConsentCookieStorageError(
      'invalid_lifetime',
      `Consent lifetime must be an integer between 1 and ${MAX_CONSENT_LIFETIME_DAYS} days.`,
    );
  }
}

function resolveDocument(
  candidate: CookieDocument | undefined,
): CookieDocument {
  if (candidate) {
    return candidate;
  }

  if (typeof globalThis.document !== 'undefined') {
    return globalThis.document;
  }

  throw new ConsentCookieStorageError(
    'unavailable_document',
    'Browser cookie storage requires a document implementation.',
  );
}

function readCookieValue(cookieHeader: string, cookieName: string): string | null {
  for (const part of cookieHeader.split(';')) {
    const candidate = part.trim();
    const separatorIndex = candidate.indexOf('=');

    if (separatorIndex < 0) {
      continue;
    }

    if (candidate.slice(0, separatorIndex) === cookieName) {
      return candidate.slice(separatorIndex + 1);
    }
  }

  return null;
}

function assertValidNow(now: Date): void {
  if (Number.isNaN(now.getTime())) {
    throw new ConsentCookieStorageError(
      'invalid_now',
      'Cookie clock must return a valid Date.',
    );
  }
}

export function createConsentCookieStorage(
  options: ConsentCookieStorageOptions,
): ConsentCookieStorage {
  validateOptions(options);

  const cookieDocument = resolveDocument(options.document);
  const cookieName = options.cookieName ?? CONSENT_COOKIE_NAME;
  const secure = options.secure ?? true;
  const maxAgeSeconds = options.consentLifetimeDays * SECONDS_PER_DAY;
  const now = options.now ?? (() => new Date());
  const securityAttributes = secure
    ? '; Path=/; SameSite=Lax; Secure'
    : '; Path=/; SameSite=Lax';

  return Object.freeze({
    read(): ConsentCookieDecodeResult {
      return decodeConsentCookie(
        readCookieValue(cookieDocument.cookie, cookieName),
      );
    },

    write(state: ConsentState): void {
      const currentTime = now();
      assertValidNow(currentTime);

      const expires = new Date(
        currentTime.getTime() + maxAgeSeconds * 1000,
      );
      assertValidNow(expires);

      cookieDocument.cookie =
        `${cookieName}=${encodeConsentCookie(state)}` +
        `; Max-Age=${maxAgeSeconds}` +
        `; Expires=${expires.toUTCString()}` +
        securityAttributes;
    },

    clear(): void {
      cookieDocument.cookie =
        `${cookieName}=` +
        '; Max-Age=0' +
        '; Expires=Thu, 01 Jan 1970 00:00:00 GMT' +
        securityAttributes;
    },
  });
}

